import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";

type IconButtonVariant = "outline" | "ghost" | "danger" | "success";
type IconButtonSize = "xs" | "sm" | "md" | "lg";
type IconButtonShape = "square" | "round";

export type IconButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  icon: ReactNode;
  ariaLabel: string;
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  shape?: IconButtonShape;
};

const variantClasses: Record<IconButtonVariant, string> = {
  outline:
    "border border-[color:var(--border-light)] text-[color:var(--text-muted)] hover:border-[color:var(--border-strong)] hover:text-[color:var(--text-primary)]",
  ghost:
    "border border-transparent text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]",
  danger:
    "border border-[color:var(--border-light)] text-[color:var(--error-500)] hover:border-[color:var(--error-500)]",
  success:
    "border border-[color:var(--border-light)] text-[color:var(--success-500)] hover:border-[color:var(--success-500)]",
};

const sizeClasses: Record<IconButtonSize, string> = {
  xs: "h-7 w-7",
  sm: "h-8 w-8",
  md: "h-9 w-9",
  lg: "h-10 w-10",
};

const shapeClasses: Record<IconButtonShape, string> = {
  square: "rounded-[var(--radius-control,8px)]",
  round: "rounded-full",
};

function cx(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      type = "button",
      ariaLabel,
      icon,
      variant = "outline",
      size = "md",
      shape = "square",
      className,
      ...props
    },
    ref,
  ) => (
    <button
      ref={ref}
      type={type}
      aria-label={ariaLabel}
      data-ui="icon-button"
      data-variant={variant}
      className={cx(
        "inline-flex items-center justify-center transition disabled:cursor-not-allowed disabled:opacity-60",
        variantClasses[variant],
        sizeClasses[size],
        shapeClasses[shape],
        className,
      )}
      {...props}
    >
      <span aria-hidden="true" className="inline-flex">
        {icon}
      </span>
    </button>
  ),
);

IconButton.displayName = "IconButton";
